import { DOCUMENT_TYPES, EPS_LIST, REGIMEN_OPTS, BLOOD_TYPES, GENDER_OPTS, BRUSHING_OPTS } from "@/lib/api";

export const EMPTY_PATIENT = {
  name: "", document_type: "CC", document_number: "",
  birth_date: "", gender: "",
  phone: "", email: "", address: "", city: "",
  emergency_contact_name: "", emergency_contact_phone: "",
  eps: "", regimen: "", blood_type: "", occupation: "",
  allergies: "", chronic_conditions: "", current_medications: "",
  previous_surgeries: "", pregnant: false, smoker: false,
  last_dental_visit: "", brushing_frequency: "", dental_history: "",
  notes: "",
};

function Section({ title, children }) {
  return (
    <div className="border border-border rounded-sm p-4 sm:p-5 bg-card space-y-4">
      <div className="label-eyebrow">{title}</div>
      {children}
    </div>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <label className="label-eyebrow block mb-2">{label}</label>
      {children}
    </div>
  );
}

export default function PatientFormFields({ form, setForm }) {
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const updBool = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.checked }));
  return (
    <div className="space-y-4">
      <Section title="Identificación">
        <Field label="Nombre completo *">
          <input data-testid="pf-name" required className="input-flat" value={form.name || ""} onChange={upd("name")} />
        </Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Tipo doc.">
            <select className="input-flat" value={form.document_type || "CC"} onChange={upd("document_type")} data-testid="pf-doctype">
              {DOCUMENT_TYPES.map((d) => <option key={d.value} value={d.value}>{d.value}</option>)}
            </select>
          </Field>
          <Field label="Número documento" className="col-span-2">
            <input className="input-flat font-mono" value={form.document_number || ""} onChange={upd("document_number")} data-testid="pf-docnum" />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Fecha de nacimiento">
            <input type="date" className="input-flat font-mono" value={form.birth_date || ""} onChange={upd("birth_date")} data-testid="pf-birth" />
          </Field>
          <Field label="Género">
            <select className="input-flat" value={form.gender || ""} onChange={upd("gender")} data-testid="pf-gender">
              {GENDER_OPTS.map((g) => <option key={g.value} value={g.value}>{g.label}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Ocupación">
          <input className="input-flat" value={form.occupation || ""} onChange={upd("occupation")} />
        </Field>
      </Section>

      <Section title="Contacto">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Teléfono">
            <input className="input-flat font-mono" value={form.phone || ""} onChange={upd("phone")} data-testid="pf-phone" />
          </Field>
          <Field label="Correo">
            <input type="email" className="input-flat" value={form.email || ""} onChange={upd("email")} data-testid="pf-email" />
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Dirección" className="col-span-2">
            <input className="input-flat" value={form.address || ""} onChange={upd("address")} />
          </Field>
          <Field label="Ciudad">
            <input className="input-flat" value={form.city || ""} onChange={upd("city")} />
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Contacto de emergencia">
            <input className="input-flat" placeholder="Nombre" value={form.emergency_contact_name || ""} onChange={upd("emergency_contact_name")} />
          </Field>
          <Field label="Teléfono emergencia">
            <input className="input-flat font-mono" value={form.emergency_contact_phone || ""} onChange={upd("emergency_contact_phone")} />
          </Field>
        </div>
      </Section>

      <Section title="EPS y aseguramiento">
        <div className="grid grid-cols-2 gap-3">
          <Field label="EPS">
            <input list="eps-list" className="input-flat" value={form.eps || ""} onChange={upd("eps")} data-testid="pf-eps" />
            <datalist id="eps-list">
              {EPS_LIST.map((e) => <option key={e} value={e} />)}
            </datalist>
          </Field>
          <Field label="Régimen">
            <select className="input-flat" value={form.regimen || ""} onChange={upd("regimen")} data-testid="pf-regimen">
              {REGIMEN_OPTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Tipo de sangre">
          <select className="input-flat w-32" value={form.blood_type || ""} onChange={upd("blood_type")}>
            <option value="">—</option>
            {BLOOD_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
        </Field>
      </Section>

      <Section title="Antecedentes médicos">
        <Field label="Alergias">
          <input className="input-flat" placeholder="Penicilina, látex, anestesia…" value={form.allergies || ""} onChange={upd("allergies")} />
        </Field>
        <Field label="Enfermedades crónicas">
          <input className="input-flat" placeholder="Diabetes, hipertensión, asma…" value={form.chronic_conditions || ""} onChange={upd("chronic_conditions")} />
        </Field>
        <Field label="Medicamentos actuales">
          <input className="input-flat" placeholder="Cuáles y dosis" value={form.current_medications || ""} onChange={upd("current_medications")} />
        </Field>
        <Field label="Cirugías previas">
          <input className="input-flat" value={form.previous_surgeries || ""} onChange={upd("previous_surgeries")} />
        </Field>
        <div className="flex flex-wrap gap-4 pt-1">
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={!!form.pregnant} onChange={updBool("pregnant")} />
            Embarazo
          </label>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="w-4 h-4 accent-primary" checked={!!form.smoker} onChange={updBool("smoker")} />
            Fumador(a)
          </label>
        </div>
      </Section>

      <Section title="Antecedentes odontológicos">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Última visita">
            <input type="date" className="input-flat font-mono" value={form.last_dental_visit || ""} onChange={upd("last_dental_visit")} />
          </Field>
          <Field label="Cepillado">
            <select className="input-flat" value={form.brushing_frequency || ""} onChange={upd("brushing_frequency")}>
              {BRUSHING_OPTS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
            </select>
          </Field>
        </div>
        <Field label="Historia odontológica">
          <textarea rows={3} className="input-flat h-auto py-2"
            placeholder="Sangrado de encías, sensibilidad, bruxismo, prótesis, ortodoncia previa…"
            value={form.dental_history || ""} onChange={upd("dental_history")} />
        </Field>
      </Section>

      <Section title="Notas clínicas">
        <textarea rows={4} className="input-flat h-auto py-2" value={form.notes || ""} onChange={upd("notes")} />
      </Section>
    </div>
  );
}
